import React, { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { MapComponent } from '../Map/MapComponent'
import { ParkingList } from '../Parking/ParkingList'
import { ParkingInfoPanel } from '../Parking/ParkingInfoPanel'
import { Header } from '../Layout/Header'
import { ParkingSpot, mapsService } from '../../services/mapsServiceNew'
import { ParkedVehicleCard } from './ParkedVehicleCard'
import AnalyticsService from '../../services/analyticsService'
import { useLocalization } from '../../contexts/LocalizationContext'
import { useAudit } from '../../hooks/useAudit'
import { ParkedVehicle, UserLicensePlate, supabase } from '../../lib/supabase'

export function Dashboard() {
  const { user, hasActiveSubscription } = useAuth()
  const navigate = useNavigate()
  const { t } = useLocalization()
  const { logDashboardAccess } = useAudit()
  const [parkingSpots, setParkingSpots] = useState<ParkingSpot[]>([])
  const [selectedSpot, setSelectedSpot] = useState<ParkingSpot | null>(null)
  const [clickedSpot, setClickedSpot] = useState<ParkingSpot | null>(null)
  const [showList, setShowList] = useState(false)
  const [parkedVehicle, setParkedVehicle] = useState<ParkedVehicle | null>(null)
  const [userLicensePlate, setUserLicensePlate] = useState<UserLicensePlate | null>(null)
  const [licensePlateInput, setLicensePlateInput] = useState('')
  const [isLicensePlateModalOpen, setIsLicensePlateModalOpen] = useState(false)
  const [isSavingLicensePlate, setIsSavingLicensePlate] = useState(false)
  const [licensePlateError, setLicensePlateError] = useState<string | null>(null)
  const [parkingDuration, setParkingDuration] = useState<string>('00:00:00')
  const [isParking, setIsParking] = useState(false)
  const [showPaygStopConfirmModal, setShowPaygStopConfirmModal] = useState(false)
  const [selectedParkingLocation, setSelectedParkingLocation] = useState<{ latitude: number; longitude: number } | null>(null)

  const queryMode = new URLSearchParams(window.location.search).get('sub') || new URLSearchParams(window.location.search).get('mode')
  const persistedMode = sessionStorage.getItem('preferred_subscription_mode') || localStorage.getItem('preferred_subscription_mode')
  const isDirect2Mode = queryMode === 'direct2' || persistedMode === 'direct2'

  // Log dashboard access on mount
  useEffect(() => {
    logDashboardAccess()
    fetchParkedVehicle()
  }, [logDashboardAccess])

  useEffect(() => {
    let interval: NodeJS.Timeout
    if (parkedVehicle) {
      interval = setInterval(() => {
        const start = new Date(parkedVehicle.parked_at).getTime()
        const now = new Date().getTime()
        const diff = now - start

        const paygEndFromDb = parkedVehicle.expires_at
        const paygEndFromLocal = localStorage.getItem(`payg_end_${parkedVehicle.id}`)
        const expectedEndTimeStr = paygEndFromDb || paygEndFromLocal

        if (expectedEndTimeStr) {
          // Timer dégressif prioritaire dès qu'une fin de stationnement PAYG existe,
          // même si l'utilisateur est désormais abonné après le flow SubDirect.
          const expectedEnd = new Date(expectedEndTimeStr).getTime()
          const remaining = expectedEnd - now

          if (remaining > 0) {
            const hours = Math.floor(remaining / (1000 * 60 * 60))
            const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60))
            const seconds = Math.floor((remaining % (1000 * 60)) / 1000)

            setParkingDuration(
              `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
            )
          } else {
            setParkingDuration("00:00:00")
          }
        } else {
          // Timer progressif par défaut
          const hours = Math.floor(diff / (1000 * 60 * 60))
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
          const seconds = Math.floor((diff % (1000 * 60)) / 1000)
          
          setParkingDuration(
            `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
          )
        }
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [parkedVehicle, hasActiveSubscription])

  const getPaygEndTime = (vehicle: ParkedVehicle | null): string | null => {
    if (!vehicle) return null
    const paygEndFromDb = vehicle.expires_at
    const paygEndFromLocal = localStorage.getItem(`payg_end_${vehicle.id}`)
    return paygEndFromDb || paygEndFromLocal
  }

  const fetchParkedVehicle = async () => {
    const vehicle = await mapsService.getParkedVehicle()
    setParkedVehicle(vehicle)
  }

  const fetchUserLicensePlate = useCallback(async () => {
    if (!user?.id) return

    try {
      const { data, error } = await supabase
        .from('user_license_plates')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()

      if (error) throw error

      if (data) {
        setUserLicensePlate(data as UserLicensePlate)
        setLicensePlateInput((data as UserLicensePlate).license_plate)
        setIsLicensePlateModalOpen(false)
      } else {
        setUserLicensePlate(null)
        setLicensePlateInput('')
        setIsLicensePlateModalOpen(true)
      }
    } catch (error) {
      console.error('Failed to fetch user license plate:', error)
      setIsLicensePlateModalOpen(true)
    }
  }, [user?.id])

  useEffect(() => {
    if (!user?.id) return
    fetchUserLicensePlate()
  }, [user?.id, fetchUserLicensePlate])

  const handleOpenLicensePlateModal = () => {
    setLicensePlateInput(userLicensePlate?.license_plate || '')
    setLicensePlateError(null)
    setIsLicensePlateModalOpen(true)
  }

  const handleSaveLicensePlate = async () => {
    if (!user?.id) return

    const normalizedPlate = licensePlateInput.trim().toUpperCase()
    if (!normalizedPlate) {
      setLicensePlateError(t('dashboard.licensePlateModal.requiredError'))
      return
    }

    try {
      setIsSavingLicensePlate(true)
      setLicensePlateError(null)

      const { data, error } = await supabase
        .from('user_license_plates')
        .upsert(
          {
            user_id: user.id,
            license_plate: normalizedPlate,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        )
        .select('*')
        .single()

      if (error) throw error

      setUserLicensePlate(data as UserLicensePlate)
      setLicensePlateInput((data as UserLicensePlate).license_plate)
      setIsLicensePlateModalOpen(false)
    } catch (error) {
      console.error('Failed to save user license plate:', error)
      setLicensePlateError(t('dashboard.licensePlateModal.saveError'))
    } finally {
      setIsSavingLicensePlate(false)
    }
  }

  const handleParkVehicle = async () => {
    // Dans le mode Direct2, sans abonnement, on doit vérifier si une session Pay As You Go 
    // vient d'être initiée ou s'il faut rediriger vers le paiement
    const paygEndTime = getPaygEndTime(parkedVehicle)
    const isPaygSessionActive = !!paygEndTime && new Date(paygEndTime).getTime() > Date.now()

    if (!hasActiveSubscription() && !isPaygSessionActive) {
      // Passer l'immat et la localisation à la page de paiement
      if (userLicensePlate?.license_plate) {
        sessionStorage.setItem('parking_plate', userLicensePlate.license_plate)
      }
      try {
        const loc = selectedParkingLocation || await mapsService.getCurrentLocation()
        console.log('📍 Localisation pour paiement:', loc.latitude, loc.longitude)
        sessionStorage.setItem('parking_lat', String(loc.latitude))
        sessionStorage.setItem('parking_lng', String(loc.longitude))
      } catch (e) {
        console.warn('⚠️ Impossible de récupérer la localisation pour le header paiement', e)
      }
      navigate('/subscription?sub=direct2')
      return
    }

    try {
      setIsParking(true)
      // Artificial delay for better UX/Animation visibility
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      const location = selectedParkingLocation || await mapsService.getCurrentLocation()
      const vehicle = await mapsService.parkVehicle(location)
      setParkedVehicle(vehicle)
      setSelectedParkingLocation(null)
    } catch (error) {
      console.error('Failed to park vehicle:', error)
    } finally {
      setIsParking(false)
    }
  }

  const handleStopParking = async () => {
    if (!parkedVehicle) return

    const paygEndTime = getPaygEndTime(parkedVehicle)
    const isPaygSessionActive = !!paygEndTime && new Date(paygEndTime).getTime() > Date.now()

    if (isPaygSessionActive) {
      setShowPaygStopConfirmModal(true)
      return
    }

    try {
      await mapsService.stopParking(parkedVehicle.id)
      localStorage.removeItem(`payg_end_${parkedVehicle.id}`)
      setParkedVehicle(null)
    } catch (error) {
      console.error('Failed to stop parking:', error)
    }
  }

  const confirmStopPaygParking = async () => {
    if (!parkedVehicle) return

    try {
      await mapsService.stopParking(parkedVehicle.id)
      localStorage.removeItem(`payg_end_${parkedVehicle.id}`)
      setParkedVehicle(null)
      setShowPaygStopConfirmModal(false)
    } catch (error) {
      console.error('Failed to stop parking:', error)
    }
  }

  const handleNavigateToVehicle = () => {
    if (!parkedVehicle) return
    const url = `https://www.google.com/maps/dir/?api=1&destination=${parkedVehicle.latitude},${parkedVehicle.longitude}`
    window.open(url, '_blank')
  }

  const handleParkingSpotsFound = (spots: ParkingSpot[]) => {
    setParkingSpots(spots)
    
    // Tracker la recherche de parking
    AnalyticsService.trackParkingSearch()
    AnalyticsService.trackEvent('parking_spots_found', {
      event_category: 'parking',
      event_label: 'search_results',
      value: spots.length,
    })
  }

  const handleSelectSpot = (spot: ParkingSpot) => {
    setSelectedSpot(spot)
    setShowList(false) // Hide list on mobile when spot is selected
    
    // Tracker la sélection d'une place de parking
    AnalyticsService.trackEvent('parking_spot_selected', {
      event_category: 'parking',
      event_label: spot.name || 'unknown_spot',
      value: spot.price_level || 0,
    })
  }

  const handleToggleView = () => {
    setShowList(!showList)
    
    // Tracker le changement de vue
    AnalyticsService.trackEvent('view_toggle', {
      event_category: 'interface',
      event_label: showList ? 'map_view' : 'list_view',
    })
  }

  const paygStopConfirmModal = showPaygStopConfirmModal && typeof document !== 'undefined'
    ? createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[1000001] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {t('dashboard.paygStopConfirm.title')}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              {t('dashboard.paygStopConfirm.message')}
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowPaygStopConfirmModal(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                {t('dashboard.paygStopConfirm.cancel')}
              </button>
              <button
                onClick={confirmStopPaygParking}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                {t('dashboard.paygStopConfirm.confirm')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <Header 
        showToggleButton={true}
        onToggleView={handleToggleView}
        showList={showList}
        onEditLicensePlate={handleOpenLicensePlateModal}
      />

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Map section */}
        <div className={`flex-1 ${showList ? 'hidden md:block' : 'block'}`}>
          <MapComponent
            onParkingSpotsFound={handleParkingSpotsFound}
            selectedSpot={selectedSpot}
            onMarkerClick={setClickedSpot}
            parkedVehicle={parkedVehicle}
            onMapClick={setSelectedParkingLocation}
            selectedParkingLocation={selectedParkingLocation}
          />
        </div>

        {/* Park Here Button / Info Panel */}
        <div className={`fixed bottom-8 md:bottom-8 left-1/2 md:left-[calc(50%-12rem)] transform -translate-x-1/2 z-[10002] w-[calc(100%-2rem)] max-w-md transition-all duration-300 ${showList ? 'opacity-0 pointer-events-none md:opacity-100 md:pointer-events-auto' : 'opacity-100'}`}>
          {!parkedVehicle ? (
            <div className="space-y-3">
              {selectedParkingLocation && (
                <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/40 px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-[0.18em] text-emerald-600">
                        {t('dashboard.selectedLocation')}
                      </p>
                      <p className="text-sm text-slate-700 font-medium mt-1">
                        {selectedParkingLocation.latitude.toFixed(5)}, {selectedParkingLocation.longitude.toFixed(5)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {t('dashboard.selectedLocationHint')}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedParkingLocation(null)}
                      className="text-xs font-bold text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      {t('dashboard.clearSelectedLocation')}
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={handleParkVehicle}
                className="w-full bg-emerald-500 text-white font-black py-4 sm:py-5 rounded-[1.75rem] shadow-2xl shadow-emerald-500/40 hover:bg-emerald-600 hover:shadow-emerald-500/50 transform active:scale-[0.98] transition-all duration-300 flex items-center justify-center text-[19px] sm:text-[20px] border border-emerald-400/50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {t('dashboard.parkHere')}
              </button>
            </div>
          ) : (
            <ParkedVehicleCard
              parkedVehicle={parkedVehicle}
              parkingDuration={parkingDuration}
              onNavigate={handleNavigateToVehicle}
              onStop={handleStopParking}
              t={t}
            />
          )}
        </div>

        {/* Parking list section */}
        <div className={`w-full md:w-96 bg-white border-l border-gray-200 ${
          showList ? 'block' : 'hidden md:block'
        }`}>
          <ParkingList
            parkingSpots={parkingSpots}
            selectedSpot={selectedSpot}
            onSelectSpot={handleSelectSpot}
          />
        </div>
      </div>

      {/* Parking Info Panel */}
      <ParkingInfoPanel
        parking={clickedSpot}
        onClose={() => setClickedSpot(null)}
      />

      {/* Parking Animation Overlay */}
      {isParking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 flex flex-col items-center shadow-2xl max-w-sm w-full mx-4">
            <div className="relative w-24 h-24 mb-6">
              <div className="absolute inset-0 bg-paybyphone-green opacity-20 rounded-full animate-ping"></div>
              <div className="absolute inset-2 bg-paybyphone-green opacity-40 rounded-full animate-pulse"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-paybyphone-green" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
              {t('dashboard.parkHere') === 'STATIONNER ICI' ? 'Enregistrement...' : 'Recording location...'}
            </h3>
            <p className="text-gray-500 text-center">
              {selectedParkingLocation
                ? (t('dashboard.selectedLocationHint') || 'Using selected point on map')
                : (t('dashboard.parkHere') === 'STATIONNER ICI' ? 'Nous sécurisons votre position' : 'Securing your parking spot')}
            </p>
          </div>
        </div>
      )}

      {isLicensePlateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {t('dashboard.licensePlateModal.title')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('dashboard.licensePlateModal.description')}
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('dashboard.licensePlateModal.label')}
            </label>
            <input
              type="text"
              value={licensePlateInput}
              onChange={(e) => setLicensePlateInput(e.target.value.toUpperCase())}
              placeholder={t('dashboard.licensePlateModal.placeholder')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-paybyphone-green focus:border-paybyphone-green"
              maxLength={20}
            />

            {licensePlateError && (
              <p className="text-sm text-red-600 mt-2">{licensePlateError}</p>
            )}

            <div className="mt-5 flex justify-end">
              <button
                onClick={handleSaveLicensePlate}
                disabled={isSavingLicensePlate}
                className="bg-paybyphone-green text-white px-4 py-2 rounded-lg font-medium hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingLicensePlate
                  ? t('dashboard.licensePlateModal.saving')
                  : t('dashboard.licensePlateModal.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {paygStopConfirmModal}
    </div>
  )
}
